import { createFileRoute } from '@tanstack/react-router';
import { Group, Stack, Text } from '@mantine/core';
import { IconFile, IconUpload, IconX } from '@tabler/icons-react';
import { useState } from 'react';
import { Dropzone } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import { packageListQueryOptions } from '@api/deployment/packageQueries';
import { useQuery } from '@tanstack/react-query';
import { PackageTable } from '@features/deployment/components/packages/PackageTable';
import { PackageUploadModal } from '@features/deployment/components/packages/PackageUploadModal';
import { DeploymentLayout } from '@features/deployment/components/DeploymentLayout';
import { AdminPendingComponent } from '@features/admin/components/AdminPendingComponent';
import { AdminErrorComponent } from '@features/admin/components/AdminErrorComponent';

export const Route = createFileRoute('/_authed/admin/deployment-center/packages/')({
  loader: ({context: {queryClient}}) =>
    queryClient.ensureQueryData(packageListQueryOptions()),
  pendingComponent: AdminPendingComponent,
  errorComponent: AdminErrorComponent,
  component: PackagesPage,
});

function PackagesPage() {
  const [uploadModalOpened, setUploadModalOpened] = useState(false);
  const [preDroppedFile, setPreDroppedFile] = useState<File | null>(null);
  const {data: packagesResponse, isLoading, refetch} = useQuery(packageListQueryOptions());

  const packages = packagesResponse?.packages || [];

  const handleDrop = (files: File[]) => {
    if (files.length > 0) {
      if (!files[0].name.toLowerCase().endsWith('.msi')) {
        notifications.show({
          title: 'Invalid file type',
          message: 'Only MSI files are supported. Please drop an .msi file.',
          color: 'red',
        });
        return;
      }
      setPreDroppedFile(files[0]);
      setUploadModalOpened(true);
    }
  };

  const handleModalClose = () => {
    setUploadModalOpened(false);
    setPreDroppedFile(null);
  };

  return (
    <DeploymentLayout>
      <Stack gap="md">
        <Dropzone
          onDrop={handleDrop}
          maxSize={500 * 1024 * 1024} // 500MB limit
          multiple={false}
          aria-label="upload-package-dropzone"
        >
          <Group justify="center" gap="xl" mih={100} style={{pointerEvents: 'none'}}>
            <Dropzone.Accept>
              <IconUpload size={52} color="var(--mantine-color-blue-6)" stroke={1.5}/>
            </Dropzone.Accept>
            <Dropzone.Reject>
              <IconX size={52} color="var(--mantine-color-red-6)" stroke={1.5}/>
            </Dropzone.Reject>
            <Dropzone.Idle>
              <IconFile size={52} color="var(--mantine-color-dimmed)" stroke={1.5}/>
            </Dropzone.Idle>

            <div>
              <Text size="xl" inline>
                Drop MSI files here or click to upload
              </Text>
              <Text size="sm" c="dimmed" inline mt={7}>
                Upload CadCare Connect Agent packages (max 500MB)
              </Text>
            </div>
          </Group>
        </Dropzone>

        <PackageTable packages={packages}
                      loading={isLoading}
                      onRefresh={() => refetch()}
        />

        <PackageUploadModal
          opened={uploadModalOpened}
          onClose={handleModalClose}
          preDroppedFile={preDroppedFile}
        />
      </Stack>
    </DeploymentLayout>
  );
}